import { useState } from "react";
import AudioPlayer from "@/components/ui/audio-player";
import type { ReviewAnswer } from "../types";
import VideoModal from "./video-modal";

interface FormAnswerItemProps {
	answer: ReviewAnswer;
}

export default function FormAnswerItem({ answer }: FormAnswerItemProps) {
	const [videoModalOpen, setVideoModalOpen] = useState(false);

	// Get icon for answer type
	const renderTypeIcon = () => {
		switch (answer.content_type) {
			case "video":
				return (
					<div className="flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-purple-50 text-purple-700 border border-purple-200">
						<svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
							<path
								strokeLinecap="round"
								strokeLinejoin="round"
								strokeWidth={2}
								d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"
							/>
						</svg>
						<span className="text-xs font-medium">Video</span>
					</div>
				);
			case "audio":
				return (
					<div className="flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-blue-50 text-blue-700 border border-blue-200">
						<svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
							<path
								strokeLinecap="round"
								strokeLinejoin="round"
								strokeWidth={2}
								d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z"
							/>
						</svg>
						<span className="text-xs font-medium">Audio</span>
					</div>
				);
			case "text":
				return (
					<div className="flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-green-50 text-green-700 border border-green-200">
						<svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
							<path
								strokeLinecap="round"
								strokeLinejoin="round"
								strokeWidth={2}
								d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
							/>
						</svg>
						<span className="text-xs font-medium">Text</span>
					</div>
				);
			default:
				return null;
		}
	};

	// Render answer content based on type
	const renderAnswerContent = () => {
		switch (answer.content_type) {
			case "video":
				if (answer.mux_playback_id || answer.video_url) {
					const thumbnailUrl = answer.video_thumbnail_url || (answer.mux_playback_id ? `https://image.mux.com/${answer.mux_playback_id}/thumbnail.jpg?time=0` : null);
					const videoUrl = answer.mux_playback_id ? `https://stream.mux.com/${answer.mux_playback_id}` : answer.video_url;
					const hasMuxPlayback = !!answer.mux_playback_id;

					return (
						<>
							<div className="rounded-lg overflow-hidden border border-gray-200 bg-gray-900">
								<div className="aspect-video relative">
									{thumbnailUrl ? (
										<img src={thumbnailUrl} alt="Video thumbnail" className="w-full h-full object-cover" />
									) : (
										<div className="w-full h-full bg-gradient-to-br from-gray-700 to-gray-900 flex items-center justify-center">
											<svg className="w-12 h-12 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
												<path
													strokeLinecap="round"
													strokeLinejoin="round"
													strokeWidth={1.5}
													d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"
												/>
											</svg>
										</div>
									)}
									<div className="absolute inset-0 flex items-center justify-center bg-black/30">
										{hasMuxPlayback ? (
											<button
												type="button"
												onClick={() => setVideoModalOpen(true)}
												className="w-14 h-14 rounded-full bg-white/90 flex items-center justify-center hover:bg-white transition-colors cursor-pointer"
											>
												<svg className="w-6 h-6 text-gray-600 ml-1" fill="currentColor" viewBox="0 0 24 24">
													<path d="M8 5v14l11-7z" />
												</svg>
											</button>
										) : (
											<a
												href={videoUrl || "#"}
												target="_blank"
												rel="noopener noreferrer"
												className="w-14 h-14 rounded-full bg-white/90 flex items-center justify-center hover:bg-white transition-colors"
											>
												<svg className="w-6 h-6 text-gray-600 ml-1" fill="currentColor" viewBox="0 0 24 24">
													<path d="M8 5v14l11-7z" />
												</svg>
											</a>
										)}
									</div>
								</div>
							</div>
							{hasMuxPlayback && <VideoModal open={videoModalOpen} onOpenChange={setVideoModalOpen} playbackId={answer.mux_playback_id!} />}
						</>
					);
				}
				return <span className="text-sm text-tertiary italic">No video provided</span>;

			case "audio":
				if (answer.audio_url) {
					return <AudioPlayer src={answer.audio_url} initialDuration={answer.audio_duration} />;
				}
				return <span className="text-sm text-tertiary italic">No audio provided</span>;

			case "text":
				if (answer.content_text) {
					return (
						<div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
							<p className="text-sm text-primary whitespace-pre-wrap leading-relaxed">{answer.content_text}</p>
						</div>
					);
				}
				return <span className="text-sm text-tertiary italic">No response provided</span>;

			default:
				return <span className="text-sm text-tertiary italic">No response provided</span>;
		}
	};

	return (
		<div className="p-6">
			{/* Question */}
			<div className="mb-3">
				<div className="flex items-start justify-between gap-3 mb-1.5">
					<h4 className="text-sm font-medium text-primary flex-1">{answer.form_question_title}</h4>
					{renderTypeIcon()}
				</div>
			</div>

			{/* Answer */}
			<div>{renderAnswerContent()}</div>
		</div>
	);
}
